import { Receipt } from '../device/types';
import { uuid } from '../../utils/uuid';

enum AppAction {
  ORDER_ADD_CUSTOMER = 'ORDER::ADD_CUSTOMER',
  ORDER_GET_CUSTOMER = 'ORDER::GET_CUSTOMER',
  ORDER_REMOVE_CUSTOMER = 'ORDER::REMOVE_CUSTOMER',
  ORDER_ADD_DISCOUNT = 'ORDER::ADD_DISCOUNT',
  ORDER_REMOVE_DISCOUNT = 'ORDER::REMOVE_DISCOUNT',
  ORDER_RETRIEVE = 'ORDER::RETRIEVE',
  SDK_AUTHORIZE = 'SDK::AUTHORIZE',
  SDK_INITIALIZE = 'SDK::INITIALIZE',
  VIEW_CONTINUE = 'VIEW::CONTINUE',
}

enum InternalAppAction {
  LOGGER_LOG = 'LOGGER::LOG',
}

/**
 * These are the actions that the Point of Sale (app) can fire off to the add-on.
 * The add-on can subscribe to these actions at the SDK initialization time and
 * define how to handle each action in its respective callback function.
 */
enum SDKAction {
  /**
   * Point of Sale will fire off this action when it needs information about the add-on
   * (e.g. when displaying the add-on name in the action menu item). The add-on can
   * define a {@link SDKGetAddonCallbackFunction} callback function to respond.
   */
  SDK_GET_ADDON_INFO = 'SDK::GET_ADDON_INFO',

  /**
   * Point of Sale will fire off this action when a barcode has been scanned along with
   * the scanned data. The add-on can define a {@link DeviceNotifyBarcodeScannedCallbackFunction}
   * to retrieve the scanned data.
   */
  DEVICE_NOTIFY_BARCODE_SCANNED = 'DEVICE::NOTIFY_BARCODE_SCANNED',

  /**
   * Point of Sale will fire off this action when generating receipts for an order.
   * The add-on can subscribe to this action by defining a
   * {@link DeviceGetReceiptsCallbackFunction} and return any additional texts to add
   * to the receipt.
   */
  DEVICE_GET_RECEIPTS = 'DEVICE::GET_RECEIPTS',
}

/**
 * Callback function for {@link SDKAction.SDK_GET_ADDON_INFO}.
 * @returns The title of the add-on.
 *
 * @example
 * const sdkGetAddonCallbackFunction = () => {
 *    return { tile: 'My Add-on' };
 * }
 */
type SDKGetAddonCallbackFunction = () => { title: string };

/**
 * Callback function for {@link SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED}. 3P add-ons
 * can define the behavior for when a code has been scanned by Point of Sale app.
 * @param scannedCode Code scanned by the Point of Sale app.
 *
 * @example
 * const deviceNotifyBarcodeScannedCallbackFunction = (scannedCode: string) => {
 *    // Get customer from scanned code.
 *    const customer = lookUpCustomerUsingScannedCode(scannedCode);
 *
 *    // Associate customer to the current order.
 *    orderApi.addCustomer(customer);
 * }
 */
type DeviceNotifyBarcodeScannedCallbackFunction = (scannedCode: string) => void;

/**
 * Callback function for {@link SDKAction.DEVICE_GET_RECEIPTS}.
 * @returns Any additional receipt texts 3P add-ons want to add to the receipt.
 *
 * @example
 * const deviceGetReceiptsCallbackFunction = async () => {
 *    // Retrieve order from SDK.
 *    const order = await orderApi.retrieve();
 *
 *    // Generate receipt text from order.
 *    const receipt = generateReceiptFromOrder(order);
 *
 *    // Return receipt to Point of Sale app.
 *    return receipt;
 * }
 */
type DeviceGetReceiptsCallbackFunction = () => Receipt | Promise<Receipt>;

const ACTION_NAME_UNKNOWN = 'UNKNOWN';

type ActionName =
  | AppAction
  | InternalAppAction
  | SDKAction
  | typeof ACTION_NAME_UNKNOWN;

enum ActionType {
  REQUEST = 'REQUEST',
  RESPONSE = 'RESPONSE',
  ERROR = 'ERROR',
}

enum ErrorCode {
  UNKNOWN = '0',
  BAD_REQUEST = '400',
  UNAUTHORIZED = '401',
  NOT_FOUND = '404',
  SDK_ERROR = '500',
}

type ErrorMessageValue = {
  errorCode?: ErrorCode;
  errorMessage: string;
};

type ActionMetadata = {
  slotId: string | undefined;
  timestamp: string;
};

abstract class Message {
  actionName: ActionName;
  metadata: ActionMetadata;
  type: ActionType;
  uuid: string;
  value?: object;

  static slotId;

  constructor(
    actionName: ActionName,
    type: ActionType,
    uuid: string,
    value?: object
  ) {
    this.actionName = actionName;
    this.type = type;
    this.uuid = uuid;
    this.value = value;

    this.metadata = {
      slotId: Message.slotId,
      timestamp: new Date(Date.now()).toISOString(),
    };
  }

  static setSlotId(slotId: string) {
    Message.slotId = slotId;
  }
}

class RequestMessage extends Message {
  constructor(actionName: ActionName);
  constructor(actionName: ActionName, value?: object);
  constructor(actionName: ActionName, uuid?: string, value?: object);

  constructor(actionName: ActionName, ...args: [object?] | [string?, object?]) {
    if (args.length <= 1) {
      super(
        actionName,
        ActionType.REQUEST,
        uuid(),
        args[0] as object | undefined
      );
    } else if (args.length === 2) {
      super(actionName, ActionType.REQUEST, args[0] ?? uuid(), args[1]);
    }
  }
}

class ResponseMessage extends Message {
  constructor(actionName: ActionName, uuid: string, value?: object) {
    super(actionName, ActionType.RESPONSE, uuid, value);
  }
}

class ErrorMessage extends Message {
  value: ErrorMessageValue;

  constructor(actionName: ActionName, uuid: string, value: ErrorMessageValue) {
    super(actionName, ActionType.ERROR, uuid, value);
  }
}

class RequestError extends Error {
  errorCode: ErrorCode;

  constructor(errorCode: ErrorCode, message: string) {
    super(message);
    this.errorCode = errorCode;
  }
}

export {
  Message,
  RequestError,
  ErrorCode,
  ErrorMessageValue,
  ActionName,
  ActionType,
  AppAction,
  ErrorMessage,
  InternalAppAction,
  RequestMessage,
  ResponseMessage,
  SDKAction,
  type SDKGetAddonCallbackFunction,
  type DeviceNotifyBarcodeScannedCallbackFunction,
  type DeviceGetReceiptsCallbackFunction,
  ACTION_NAME_UNKNOWN,
};
